import { useTranslation } from 'react-i18next';
import { inkLight, secondaryBase } from '../../../../../styles/design-tokens';
import { Text } from '../../../../../ui-library/typography/typography';
import { FiltersList } from './filters-list';
import type { ReactNode } from 'react';
import type { Epic } from '../../../../../api/tasks-client/task-client.type';

type Props = {
  epics: Epic[];
  selectedEpics: Epic[];
  onSelectEpics: (epics: Epic[]) => void;
  getItemExtraInfo?: (epic: Epic) => ReactNode;
};

/**
 * Sidebar list of contributing epics rendered in the Throughput and Cost tabs.
 *
 * Callers may pass `getItemExtraInfo` to render a tab-specific subtext (e.g.
 * the Throughput tab sums the same time-series the chart renders so the
 * sidebar count matches the chart total). When omitted, falls back to the
 * epic's `subtasks_completed_count`.
 *
 * @param {Epic[]} epics - Contributing epics available for selection
 * @param {Epic[]} selectedEpics - Currently selected epics
 * @param {(epics: Epic[]) => void} onSelectEpics - Selection change handler
 * @param {(epic: Epic) => ReactNode} [getItemExtraInfo] - Optional override for the per-epic subtext
 * @returns {JSX.Element} The rendered filter list
 */
export const FiltersListEpics = ({ epics, selectedEpics, onSelectEpics, getItemExtraInfo }: Props) => {
  const i18n = useTranslation();
  return (
    <FiltersList
      items={epics}
      selectedItems={selectedEpics}
      onSelectItems={onSelectEpics}
      getItemName={(epic) => epic.name}
      getItemLabel={(epic) => (
        <>
          <Text size="small" color={secondaryBase} style={{ lineHeight: '20px' }}>
            {epic.title}
          </Text>
        </>
      )}
      getItemDescription={(epic) => (
        <Text size="small" style={{ lineHeight: '20px' }}>
          {epic.name}
        </Text>
      )}
      getItemExtraInfo={
        getItemExtraInfo ??
        ((epic) => (
          <Text size="small" color={inkLight}>
            {`${epic.subtasks_completed_count} ${(
              epic.subtasks_completed_count === 1 ? i18n.t('common.work_unit') : i18n.t('common.work_units')
            ).toLowerCase()}`}
          </Text>
        ))
      }
    />
  );
};
